import { Component } from 'react'
import 'react-dates/initialize'
import moment from 'moment'
import { DateRangePicker } from 'react-dates'
import Timezones from '~/components/OrdersPage/Timezones'
import { saveUserSettings } from '~/utils/UserSettings'
import 'react-dates/lib/css/_datepicker.css'
import styles from '~/styles/ordersPage.datePicker.module.css'
import types from '~/types/ordersPage.ts'

export default class DateRange extends Component {
	static getInitialProps() {
		return {
			namespacesRequired: [ 'Orders', 'common' ],
		}
	}
	
	constructor(props) {
		super(props)
		this._localStorageKey = 'ordersDateRange'
		moment.locale(this.props.locale)
		const { startDate, endDate } = this._getInitialDates()
		this.state = {
			focusedInput: null,
			startDate,
			endDate,
			isNoChartDate: false,
		}
		if (typeof this.props.setDatePickerApply === 'function') {
			this.props.setDatePickerApply(this._onApply.bind(this))
		}
		this._onApply()
	}
	
	render(): JSX.Element | null {
		if (this.props.show) {
			return (
				<div
					className={styles.main}
				>
					<div
						className={(this.props.location === 'ordersPage' ? styles.isOrdersPage : null)}
					>
						<DateRangePicker
							startDate={this.state.startDate}
							endDate={this.state.endDate}
							focusedInput={this.state.focusedInput}
							onDatesChange={this._onDateChange.bind(this)}
							onFocusChange={this._onFocusChange.bind(this)}
							isOutsideRange={() => false}
							minimumNights={0}
							startDatePlaceholderText={this.props.t('common:Start Date')}
							endDatePlaceholderText={this.props.t('common:End Date')}
							startDateId="DateRangePickerStartDate"
							endDateId="DateRangePickerEndDate"
						/>
						<div
							className={styles.header}
						>
							<Timezones
			                  toShow='display'
			                  timezoneCode={this.props.timezoneCode}
				              setTimezoneCode={this.props.setTimezoneCode}
				              timezoneName={this.props.timezoneName}
				              setTimezoneName={this.props.setTimezoneName}
			                  t={this.props.t}
			                />
						</div>
					</div>
					<div
						className={styles.button + ' ' + styles.after}
						onClick={this._onApply.bind(this)}
					>
						{this.props.t('common:Apply')}
					</div>
				</div>
			)
		} else {
			return null
		}
	}

	componentDidMount() {
		setTimeout(() => {
			const { startDate, endDate } = this._getInitialDates(true)
			this.setState({ startDate, endDate })
		})
	}

	componentDidUpdate(prevProps, prevState) {
	    if (prevState.endDate !== this.state.endDate) {
	      this._onApply()
	    }
	}
	
	private _onDateChange({ startDate, endDate }): void {
		if (startDate) {
			this._toClearChartDate()
		}
		const value = { startDate, endDate }
		this.setState(value)
		this._localStorage(value)
		saveUserSettings(JSON.stringify({ id: this._localStorageKey, value: {[this._localStorageKey]: value} }))
		this.props.onAction.send({
			action: 'Date change',
	     	actionData: {
	        	file: 'components/OrdersPage/DatePicker.tsx',
	        	startDate: startDate?.toISOString().substring(0, 10),
	        	endDate: endDate?.toISOString().substring(0, 10),
	      	},
		})
	}
	
	private _onFocusChange(focusedInput): void {
		this.setState({ focusedInput })
	}
	
	private _onApply(event): void {
		if (event) {
			this._toClearChartDate()
		}
		let startDate = this.state.startDate
		let endDate = this.state.endDate
		if (!this._isDateOk(this.state.startDate)) {
			startDate = moment().subtract(1, 'months')
			endDate = moment()
			this.setState({ startDate, endDate })
		}
		if (!this._isDateOk(this.state.endDate)) {
			endDate = moment()
			this.setState({ endDate })
		}
		if (this._isDateOk(startDate) && this._isDateOk(endDate)) {
			if (typeof this.props.onSelect === 'function') {
				const startDateString = this._dateToString(startDate)
				const endDateString = this._dateToString(endDate)
				if (event) {
					this.props.onAction.send({
						action: 'Apply button',
				     	actionData: {
				        	file: 'components/OrdersPage/DatePicker.tsx',
				        	startDate: startDateString,
				        	endDate: endDateString,
				      	},
					})
				}
				this.props.onSelect({
					startDate: startDateString,
					endDate: endDateString,
				})
			}
		}
	}

	private _toClearChartDate(): void {
		this.setState({ isNoChartDate: true })
		try {
			window.localStorage.removeItem('chartToOrderList')
		} catch (e) {}
	}
	
	private _onMonth(): void {
		if (typeof this.props.onSelect === 'function') {
			let startDate = moment().subtract(1, 'months')
			let endDate = moment()
			this.setState({ startDate, endDate })
			this.props.onSelect({
				startDate: this._dateToString(startDate),
				endDate: this._dateToString(endDate),
			})

		}
	}
	
	private _onByUserRange(): void {
		if (typeof this.props.onSelect === 'function') {
			const { startDate, endDate } = this._getInitialDates()
			this.setState({ startDate, endDate })
			this.props.onSelect({
				startDate: this._dateToString(startDate),
				endDate: this._dateToString(endDate ),
			})
		}
	}
	
	private _isDateOk(date): boolean {
		let isDateOk = false
		if (date && typeof date === 'object') {
			isDateOk = true
		}
		return isDateOk
	}
	
	private _dateToString(date): boolean {
		let dateObject = moment()
		if (this._isDateOk(date)) {
			dateObject = date
		}
		return dateObject.format('YYYY-MM-DD')
	}
	
	private _getInitialDates(isOnMount) {
		let startDate = moment().subtract(1, 'months')
		let endDate = moment()
		const { startDate: lsStartDate, endDate: lsEndDate } = this._localStorage()
		startDate = moment(this.props.chartDate || lsStartDate || startDate)
		endDate = moment(this.props.chartDate || endDate)
		const isEndDateSaved = (this.props.saveEndDate && !this.props.chartDate)
		if (isEndDateSaved) {
			endDate = moment(lsEndDate)
		}
		if (isOnMount) {
			if ((!this.props.chartDate && !isEndDateSaved) || this.props.route === '/shop/dashboard') {
				const value = { startDate, endDate }
				this._localStorage(value)
				saveUserSettings(JSON.stringify({ id: this._localStorageKey, value: {[this._localStorageKey]: value} }))
			}
			if (this.props.setIsDatePickerSet) {
				this.props.setIsDatePickerSet(true)
			}
		}
		return { startDate, endDate }
	}
	
	private _localStorage(value?: any, key?: string): null | types.dealerData {
		try {
			if (window && window.localStorage) {
				if (!key) {
					key = this._localStorageKey
				}
				if (value) {
					window.localStorage.setItem(key, JSON.stringify(value))
				} else {
					return JSON.parse(window.localStorage.getItem(key)) || {}
				}
				
			}
		} catch(e) {
			return {}
		}
	}
}